"use client";

import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { Banner } from "@/components/Banner";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";
import { BindableInput } from "./BindableInput";
import { cn } from "@/lib/utils";

// `str | list[str]` fields may still hold a list saved by the chip-list
// widget they replaced. Show it as CSV text, quoting entries that contain a
// comma or quote, so the backend's CSV parse (`parse_filter_terms`) reads an
// edited value back into the same terms.
function listAsCsvText(items: string[]): string {
  return items
    .map((item) =>
      /[",]/.test(item) ? `"${item.replaceAll('"', '""')}"` : item
    )
    .join(", ");
}

/**
 * V2 renderer for `display=ConfigBindableText`. Single-line bindable string:
 * literal mode is a text input matching `dataType`, variable mode shows a
 * chip when the stored value is exactly `:var[<ref>]`. The picker overlay
 * button toggles between the two.
 *
 * Companion to `BindableBigTextField` (multi-line) — they exist as separate
 * renderers because the underlying editor primitives differ; the picker
 * affordance and chip semantics are shared via `BindableInput`.
 */
export function BindableTextField({
  nodeId,
  fieldKey,
  label,
  tooltip,
  description,
  disabled,
  placeholder,
  defaultToVariable,
  dataTypeField,
  dataType: explicitDataType,
  variableOnly,
  isList,
  isRequired,
  showOptionalLabel,
  error,
}: {
  nodeId: string;
  fieldKey: string;
  label: string;
  tooltip?: string;
  description?: string;
  disabled?: boolean;
  placeholder?: string;
  defaultToVariable?: boolean;
  /** Sibling config field whose value is the picker's data type (e.g. an
   * OutputNode's `type` chips). Unset → the field is a plain string. */
  dataTypeField?: string;
  /** Element data-type the field expects, derived from its annotation. Used
   * when there is no `dataTypeField` sibling. */
  dataType?: string;
  /** Drop the literal input — the field can only reference a variable (e.g. a
   * list input that can only come from an upstream output). */
  variableOnly?: boolean;
  /** When true, the field expects a list — the picker offers only list
   * sources and a bound scalar is flagged. */
  isList?: boolean;
  isRequired?: boolean;
  showOptionalLabel?: boolean;
  error?: string;
}) {
  const form = useFormContext();
  const path = `${nodeId}.${fieldKey}`;
  const raw = form.watch(path);
  const value: string =
    typeof raw === "string"
      ? raw
      : Array.isArray(raw) && raw.every((v) => typeof v === "string")
        ? listAsCsvText(raw)
        : "";
  const siblingType = form.watch(
    dataTypeField ? `${nodeId}.${dataTypeField}` : path
  );
  const dataType =
    dataTypeField && typeof siblingType === "string"
      ? siblingType
      : (explicitDataType ?? "str");
  const resolvedLabel = useMemo(
    () => label || humanize(fieldKey),
    [label, fieldKey]
  );
  const invalid = !!error;

  return (
    <div className="flex flex-col gap-1">
      <NoxusFormLabel
        label={resolvedLabel}
        tooltip={tooltip}
        name={fieldKey}
        noMargin
        isDisabled={disabled}
        isRequired={isRequired}
        showOptionalLabel={showOptionalLabel}
      />
      <div
        className={cn(
          invalid && "rounded-md ring-1 ring-inset ring-amber-400/70"
        )}
      >
        <BindableInput
          nodeId={nodeId}
          value={value}
          dataType={dataType}
          isList={isList}
          disabled={disabled}
          placeholder={placeholder}
          defaultToVariable={defaultToVariable}
          variableOnly={variableOnly}
          inputName={fieldKey}
          onChange={(next) =>
            form.setValue(path, next, {
              shouldDirty: true,
              shouldTouch: true,
            })
          }
        />
      </div>
      {description && !error ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      {error ? <Banner label={error} style="warning" size="sm" /> : null}
    </div>
  );
}

function humanize(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}
